/**
 * <site-header>:
 * - keeps --header-height on <html> current, so a transparent header can lay
 *   over the first banner by exactly its own height;
 * - marks the header with [data-scrolled] once the page scrolls, so CSS can
 *   switch the bar to its solid, translucent, shadowed state;
 * - with data-position="reveal", sets [data-hidden] while scrolling down and
 *   clears it on the way back up.
 * A custom element keeps working when the theme editor re-renders the section.
 */
class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('.site-header__bar');
    if (this.bar) {
      this.resizeObserver = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--header-height', `${this.bar.offsetHeight}px`);
      });
      this.resizeObserver.observe(this.bar);
    }

    if (this.dataset.position === 'static') return;

    this.sentinel = document.createElement('div');
    this.sentinel.setAttribute('aria-hidden', 'true');
    this.sentinel.style.cssText = 'position:absolute;top:0;height:1px;width:1px;pointer-events:none';
    document.body.prepend(this.sentinel);

    this.observer = new IntersectionObserver(([entry]) => {
      this.toggleAttribute('data-scrolled', !entry.isIntersecting);
    });
    this.observer.observe(this.sentinel);

    if (this.dataset.position === 'reveal') {
      this.lastScrollY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.observer?.disconnect();
    this.sentinel?.remove();
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
  }

  onScroll() {
    const y = window.scrollY;
    const delta = y - this.lastScrollY;
    if (Math.abs(delta) < 4) return;

    // Stay visible near the top, and while a keyboard user is inside the header.
    const hide = delta > 0 && y > this.offsetHeight * 2 && !this.querySelector(':focus-visible');
    this.toggleAttribute('data-hidden', hide);
    this.lastScrollY = y;
  }
}

if (!customElements.get('site-header')) {
  customElements.define('site-header', SiteHeader);
}
